import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, signal, WritableSignal } from '@angular/core';
import { ButtonPadding, ButtonVariant } from '@app/components/button/button.component';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { PlIcon } from '@app/icon-library/icons';
import { LifemapType, PlCalendarEvent } from '@shared/services/lifemap/lifemap.service';
import { TimeFormat } from '@app/components/pl-timepicker/pl-timepicker.component';
import { CalendarViewHourDisplayPipe } from '@app/components/blocks/block-lifemap/pipes/calendarViewHourDisplay/calendarViewHourDisplay.pipe';
import { roundToNearestHalfHour } from '@app/utils/date';

export interface ModalConfirmEventsOverrideInputs {
  eventsOverridden: PlCalendarEvent[];
  timeFormat: TimeFormat;
}

interface EventDescription {
  dateTime: string;
  icon: PlIcon;
  title: string;
  type: LifemapType;
}

@Component({
  selector: 'pl-modal-confirm-event-override',
  templateUrl: './modalConfirmEventsOverride.component.html',
})
export class ModalConfirmEventsOverrideComponent {
  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;

  public eventsOverriddenAsString: WritableSignal<EventDescription[]> = signal([]);

  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  public readonly IconFrame: typeof IconFrame = IconFrame;
  public readonly PlIcon: typeof PlIcon = PlIcon;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalConfirmEventsOverrideInputs,
    private dialogRef: MatDialogRef<ModalConfirmEventsOverrideComponent, boolean | undefined>,
    private calendarViewHourDisplayPipe: CalendarViewHourDisplayPipe,
  ) {
    this.eventsOverriddenAsString.set(this.computeEvents(data.eventsOverridden, data.timeFormat));
  }

  public closeModal(): void {
    this.dialogRef.close(false);
  }

  public overrideEvent(): void {
    this.dialogRef.close(true);
  }

  private computeEvents(eventsOverridden: PlCalendarEvent[], timeFormat: TimeFormat): EventDescription[] {
    return eventsOverridden.map((event: PlCalendarEvent): EventDescription => {
      const dateEvent: string = `${this.getTimeFromDate(event.start, timeFormat)} ~ ${this.getTimeFromDate(
        event.end,
        timeFormat,
      )}`;

      const icon: PlIcon = this.getIconForType(event.meta.type);

      return {
        dateTime: dateEvent,
        icon,
        type: event.meta.type,
        title: event.meta.type !== LifemapType.sleep ? event.title : '',
      };
    });
  }

  private getIconForType(type: LifemapType): PlIcon {
    switch (type) {
      case LifemapType.nonWork:
        return PlIcon.heart_wind;
      case LifemapType.sleep:
        return PlIcon.moon;
      case LifemapType.work:
        return PlIcon.briefcase;
      default:
        throw new Error(`All events that we override have to be work, nonWork or sleep, here it's ${type}`);
    }
  }

  private getTimeFromDate(dateTime: Date | undefined, timeFormat: TimeFormat): string {
    if (dateTime) {
      const dateTimeRoundedToHalfHour: Date = roundToNearestHalfHour(dateTime);
      return this.calendarViewHourDisplayPipe.transform(dateTimeRoundedToHalfHour, timeFormat);
    }

    return '';
  }
}
